import {type AppState} from '../../types/app-state'
import {type UserData} from './state'

const USER_AVAILABILITY_RETRY_DELAY_MS = 1000
const USER_AVAILABILITY_RETRY_COUNT = 10

export const currentUserSelector = (state: AppState): UserData => state.currentUser.currentUser

export const isFetchingCurrentUserSelector = (state: AppState): boolean =>
    state.currentUser.isFetching

export const isInternalUserSelector = (state: AppState): boolean =>
    state.currentUser.currentUser.internalUser

/**
 * Defers the execution of a callback function until the current user is available,
 * retrying the check for user availability a specified number of times with a delay between retries.
 *
 * @param getState - A function that returns the current application state.
 * @param callback - The function to execute once the user is available.
 * @param retriesLeft - The number of retry attempts remaining before giving up (defaults to USER_AVAILABILITY_RETRY_COUNT).
 */
export function deferTillUserIsAvailable(
    getState: () => AppState,
    callback: () => void,
    retriesLeft = USER_AVAILABILITY_RETRY_COUNT,
): void {
    const state = getState()
    const isFetchingUser = isFetchingCurrentUserSelector(state)
    if (isFetchingUser) {
        if (retriesLeft <= 0) {
            console.error(
                'Failed to log audit event because user is not available after exhausting retries',
            )
            return
        }
        setTimeout(() => {
            deferTillUserIsAvailable(getState, callback, retriesLeft - 1)
        }, USER_AVAILABILITY_RETRY_DELAY_MS)
        return
    }
    callback()
}
